import { useMemo } from "react"
import {
    summarizeCurrencyStatuses,
    usePilotCurrencyStatuses,
} from "../currency/usePilotCurrencyStatuses"
import { useCertificationCounts } from "../currency/useCertificationStatuses"
import { useDroneConditionCounts } from "../currency/useDroneConditionCounts"
import { Tooltip } from "panels/content/top-bar/TODO-put-in-package/Tooltip"

const SEGMENT_CLASSES = {
    current: "bg-[#00f9a1]",
    upcoming: "bg-orange-500",
    overdue: "bg-red-500",
} as const

const SEGMENT_LABELS = {
    current: "Up to date",
    upcoming: "Upcoming",
    overdue: "Overdue",
} as const

const SEGMENT_ORDER = ["current", "upcoming", "overdue"] as const

type StatusCounts = {
    total: number
    current: number
    upcoming: number
    overdue: number
}

function StatusTooltip({ counts }: { counts: StatusCounts }) {
    return (
        <Tooltip hoverable>
            <div className="flex items-center gap-6 p-2">
                <div className="flex flex-col gap-1">
                    {SEGMENT_ORDER.map((state) => (
                        <div key={state} className="flex items-center gap-2">
                            <div
                                className={`size-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${SEGMENT_CLASSES[state]}`}
                            />
                            <small className="text-gray-300">{SEGMENT_LABELS[state]}</small>
                        </div>
                    ))}
                </div>
                <div className="flex flex-col gap-1 items-end">
                    {SEGMENT_ORDER.map((state) => (
                        <small key={state} className="font-mono text-white">
                            {counts[state]}
                        </small>
                    ))}
                </div>
            </div>
        </Tooltip>
    )
}

function StatusCard({ title, counts }: { title: string; counts: StatusCounts }) {
    const percentCurrent = Math.round((counts.current / counts.total) * 100)

    return (
        <div className="relative group/tooltip flex items-start justify-between p-4 rounded-lg hover:bg-white-100">
            <div className="flex-1 flex flex-col gap-2">
                <div className="text-[18px] telegraf text-gray-100 h-8 flex items-center">
                    {title}
                </div>
                <div className="flex flex-col gap-4 w-full">
                    <div className="flex flex-col gap-0.5">
                        <div className="text-[32px] telegraf text-white leading-[1.2]">
                            {percentCurrent}%
                        </div>
                        <small className="font-mono text-gray-100">Up to date</small>
                    </div>
                    <div className="flex gap-0.5 h-2 w-full">
                        {SEGMENT_ORDER.map(
                            (state) =>
                                counts[state] > 0 && (
                                    <div
                                        key={state}
                                        className={`h-full rounded-full ${SEGMENT_CLASSES[state]}`}
                                        style={{ flexGrow: counts[state] }}
                                    />
                                )
                        )}
                    </div>
                </div>
            </div>
            <StatusTooltip counts={counts} />
        </div>
    )
}

export function CurrencyStatusSection() {
    const { statusesByUser } = usePilotCurrencyStatuses()
    const { counts: certCounts } = useCertificationCounts()
    const conditionCounts = useDroneConditionCounts()

    const currencyCounts = useMemo<StatusCounts>(() => {
        const summaries = [...statusesByUser.values()].map(summarizeCurrencyStatuses)
        return {
            total: summaries.length,
            current: summaries.filter((s) => s.state === "current").length,
            upcoming: summaries.filter((s) => s.state === "upcoming").length,
            overdue: summaries.filter((s) => s.state === "overdue").length,
        }
    }, [statusesByUser])

    const cards = [
        { title: "Pilot Currency", counts: currencyCounts },
        { title: "Pilot Certifications", counts: certCounts },
        { title: "Drone Condition", counts: conditionCounts },
    ].filter((c) => c.counts.total > 0)

    if (cards.length === 0) return null

    return (
        <div className="flex flex-col gap-2">
            <div className="text-[22px] telegraf text-gray-50 py-2">Pilot & Fleet Status</div>
            <div className="border border-white-200 rounded-2xl p-8 flex items-center">
                {cards.map((c) => (
                    <div key={c.title} className="flex-1">
                        <StatusCard title={c.title} counts={c.counts} />
                    </div>
                ))}
            </div>
        </div>
    )
}
